import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const NotificationContext = createContext(null);

/** Pop a native browser notification (Chrome / Safari / Firefox desktop)
 *  when the tab is backgrounded. Silent no-op when the user hasn't granted
 *  permission — the in-app toast still fires in that case. */
const popDesktopNotification = (msg) => {
  try {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission !== 'granted') return;
    if (typeof document !== 'undefined' && !document.hidden) return; // toast is enough when tab is active
    const n = new Notification(msg.title || 'New notification', {
      body: msg.body || '',
      tag: msg.notification_id, // de-dupes if user gets multiple bursts
      icon: '/favicon.ico',
    });
    n.onclick = () => {
      window.focus();
      if (msg.link) window.location.href = msg.link;
      n.close();
    };
  } catch { /* ignore */ }
};

export function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const pollTimer = useRef(null);
  const wsConnected = useRef(false);

  const computeUnread = useCallback((list) => {
    return list.filter(n => !n.is_read).length;
  }, []);

  // Use ref to break circular dependency
  const stopPollingRef = useRef(() => {});

  const stopPolling = useCallback(() => {
    if (pollTimer.current) {
      clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
  }, []);
  stopPollingRef.current = stopPolling;

  // Fetch notifications via REST
  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/notifications`);
      setNotifications(data);
      setUnreadCount(computeUnread(data));
    } catch (err) {
      if (err.response?.status === 401) {
        stopPollingRef.current(); // Stop polling when auth is invalid
      }
    }
  }, [computeUnread]);

  // Polling fallback (when WS is not available)
  const startPolling = useCallback(() => {
    if (pollTimer.current) return;
    pollTimer.current = setInterval(fetchNotifications, 30000);
  }, [fetchNotifications]);

  // Connect WebSocket
  const connectWs = useCallback(() => {
    const token = sessionStorage.getItem('auth_token');
    if (!token) {
      // No token means cookies-only auth; start polling only if we can verify auth
      fetchNotifications(); // single attempt; if 401, stopPolling is called inside
      startPolling();
      return;
    }

    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsUrl = baseUrl.replace(/^http/, 'ws') + `/ws/notifications?token=${token}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        wsConnected.current = true;
        stopPolling(); // WS is working, no need for polling
        ws._pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send('ping');
        }, 30000);
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'notification') {
            setNotifications(prev => {
              const updated = [{ ...msg, is_read: false }, ...prev].slice(0, 50);
              setUnreadCount(computeUnread(updated));
              return updated;
            });

            // Fire a sonner toast for events that the user will care about
            // immediately. `email.received` is the marquee one — adjuster
            // replies should feel real-time. Toast click navigates straight
            // to the claim's Emails tab.
            if (msg.event_type === 'email.received') {
              toast(msg.title || 'New email reply', {
                description: msg.body,
                icon: <Mail className="w-4 h-4 text-blue-500" />,
                duration: 6000,
                action: msg.link ? {
                  label: 'Open',
                  onClick: () => { window.location.href = msg.link; },
                } : undefined,
              });
              popDesktopNotification(msg);
            }
          }
        } catch { /* ignore non-JSON */ }
      };

      ws.onclose = () => {
        clearInterval(ws._pingInterval);
        wsConnected.current = false;
        startPolling(); // Fallback to polling
        reconnectTimer.current = setTimeout(connectWs, 15000);
      };

      ws.onerror = () => ws.close();
    } catch {
      startPolling(); // Fallback to polling
      reconnectTimer.current = setTimeout(connectWs, 15000);
    }
  }, [computeUnread, startPolling, stopPolling]);

  useEffect(() => {
    fetchNotifications();
    connectWs();

    return () => {
      stopPolling();
      clearTimeout(reconnectTimer.current);
      if (wsRef.current) {
        clearInterval(wsRef.current._pingInterval);
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [fetchNotifications, connectWs, stopPolling]);

  const markRead = useCallback(async (notificationId) => {
    await axios.put(`${API}/notifications/${notificationId}/read`).catch(() => {});
    setNotifications(prev => {
      const updated = prev.map(n =>
        n.notification_id === notificationId ? { ...n, is_read: true } : n
      );
      setUnreadCount(computeUnread(updated));
      return updated;
    });
  }, [computeUnread]);

  const markAllRead = useCallback(async () => {
    await axios.put(`${API}/notifications/read-all`).catch(() => {});
    setNotifications(prev => {
      const updated = prev.map(n => ({ ...n, is_read: true }));
      setUnreadCount(0);
      return updated;
    });
  }, []);

  return (
    <NotificationContext.Provider value={{ notifications, unreadCount, markRead, markAllRead, fetchNotifications }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationProvider');
  return ctx;
}
